import Vue from "vue";
import axios from "axios";
import { eventBus } from "@/main";
import {
  mapWithLimit,
  uploadBatch,
  UPLOAD_CONCURRENCY
} from "@/utils/mediaUpload";
import {
  FILE_LIMIT_EXCEED,
  FILE_STORAGE_PLAN_EXPIRED,
  REDIIVIEW_BASE_URL
} from "@/constants";

// The upload tray, shared by every tracked batch. Kept outside state: the
// batches update their rows on every progress tick, and the pool holds Blobs.
// A batch started while another is still sending adds its rows after the
// others', so a row's position (its slot) never changes until the tray is
// cleared.
// Rows: { name, size, loaded, state, reason, retryable }
let trayRows = [];
// Files the tray can send again, by slot. Each carries its own batch's
// context, so a retry goes to the same patient and category it came from.
// Map<slot, { file, meta, name, remark, context: { clinicUrl, branchUrl, data, hasRemarks } }>
const retryPool = new Map();
// Batches in flight, so the tray can cancel them all.
const activeCancels = new Set();

const IDLE_UPLOAD_STATUS = {
  active: false,
  // one row per file: { slot, name, size, loaded, state, reason, retryable }
  items: [],
  done: 0,
  failed: 0,
  cancelled: 0,
  total: 0,
  percent: 0,
  failedNames: [],
  canRetry: false
};

// The tray's totals, worked out from its rows.
function summarizeRows(rows) {
  // Copies: the batches keep updating their rows outside mutations. Each
  // copy carries its slot, which the tray's retry buttons name. Batches the
  // server turned away before sending are left out: an alert explains them.
  const items = rows
    .map((row, slot) => ({ ...row, slot }))
    .filter(row => row.reason !== "rejected");
  const size = items.reduce((sum, r) => sum + r.size, 0);
  const loaded = items.reduce(
    (sum, r) => sum + (r.state === "uploaded" || r.state === "done" ? r.size : r.loaded),
    0
  );
  const failed = items.filter(r => r.state === "failed" && r.reason !== "cancelled");
  return {
    items,
    done: items.filter(r => r.state === "uploaded" || r.state === "done").length,
    failed: failed.length,
    cancelled: items.filter(r => r.reason === "cancelled").length,
    total: items.length,
    percent: size ? Math.min(100, Math.round((loaded / size) * 100)) : 0,
    failedNames: failed.map(r => r.name),
    canRetry: items.some(r => r.retryable && r.state === "failed")
  };
}

// What the server can produce a thumbnail from. Everything else stored as a
// media file (PDF, video, 3D models) keeps its own placeholder in the UI.
const THUMBNAILABLE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp"
];

// Small enough that the first tiles appear quickly, and within the server's
// per-request cap.
const THUMBNAIL_CHUNK = 6;

// Every listing presigns fresh URLs, and a fresh URL is a browser cache miss.
// Upload polling refetches every few seconds and reopening a patient refetches
// too, so each would re-download every tile. Reusing the thumbnail URL we
// already handed out keeps the browser on its cached copy. Presigned URLs live
// 15 minutes; 10 leaves a tile's @error fallback as the only cost of the edge.
const THUMB_REUSE_MS = 10 * 60 * 1000;
// "No thumbnail" is remembered briefly so polling does not make the server
// retry a failed generation every few seconds.
const NO_THUMB_REUSE_MS = 2 * 60 * 1000;
const THUMB_CACHE_PRUNE_AT = 5000;

// id -> { url: string|null, at }
const thumbCache = new Map();
// id -> Promise<string|null>, so overlapping runs never ask for the same id twice
const inflightThumbs = new Map();
// Bumped when a list is replaced, so a run for the old list stops asking.
const listGeneration = { fileList: 0, fileTemplate: 0 };

function rememberThumb(id, url) {
  if (thumbCache.size > THUMB_CACHE_PRUNE_AT) {
    thumbCache.forEach((_, key) => recallThumb(key));
  }
  thumbCache.set(id, { url, at: Date.now() });
}

function recallThumb(id) {
  const hit = thumbCache.get(id);
  if (!hit) return undefined;
  const ttl = hit.url ? THUMB_REUSE_MS : NO_THUMB_REUSE_MS;
  if (Date.now() - hit.at < ttl) return hit;
  thumbCache.delete(id);
  return undefined;
}

/**
 * Prefers thumbnail URLs already handed out this session over the fresh ones
 * in a new listing, and records the fresh ones for next time.
 */
function adoptKnownThumbnails(files) {
  files.forEach(file => {
    if (!THUMBNAILABLE_TYPES.includes(file.fileType)) return;
    const known = recallThumb(file.id);
    if (known) {
      if (known.url) file.thumb = known.url;
      file.thumbResolved = true;
    } else if (file.thumb) {
      rememberThumb(file.id, file.thumb);
    }
  });
  return files;
}

const moduleMediaFile = {
  namespaced: true,
  state: {
    fileList: [],
    fileTemplate: [],
    fileDetail: {},
    newFilesLength: 0,
    // Drives UploadProgressToast while a batch is being uploaded.
    uploadStatus: { ...IDLE_UPLOAD_STATUS },
    // Drives the same toast while files are fetched for download. `phase` is
    // "fetch" or "zip" (building the archive after every file arrived).
    downloadStatus: { active: false, done: 0, total: 0, percent: 0, phase: "fetch" }
  },
  mutations: {
    setFileList(state, data) {
      state.fileList = data;
    },
    concatFileList(state, data) {
      state.fileList = [...state.fileList, ...data];
    },
    // Writes to the file objects themselves rather than looking ids up in
    // fileList: lab screens and templates hold lists that fileList may no
    // longer contain, and those tiles must still leave the placeholder.
    applyThumbnails(state, entries) {
      entries.forEach(({ file, url }) => {
        if (url) file.thumb = url;
        file.thumbResolved = true;
      });
    },
    // A tile's thumbnail failed to load (expired presign, missing object):
    // fall back to the original and forget the URL so it is not reused.
    dropThumbnail(state, file) {
      thumbCache.delete(file.id);
      Vue.set(file, "thumb", null);
      Vue.set(file, "thumbResolved", true);
    },
    setFileTemplate(state, data) {
      state.fileTemplate = data;
    },
    concatFileTemplate(state, data) {
      state.fileTemplate = [...state.fileTemplate, ...data];
    },
    setFileDetail(state, data) {
      state.fileDetail = data;
    },
    RESET_FILE_LIST(state) {
      state.fileList = [];
    },
    setNewFileLength(state, data) {
      state.newFilesLength = data;
    },
    setUploadStatus(state, status) {
      state.uploadStatus = { ...state.uploadStatus, ...status };
    },
    setDownloadStatus(state, status) {
      state.downloadStatus = { ...state.downloadStatus, ...status };
    },
    // Puts freshly registered files into the list they belong to, replacing a
    // file with the same id (a replace upload keeps its id).
    upsertFiles(state, files) {
      files.forEach(file => {
        const key = file.category === "Template" ? "fileTemplate" : "fileList";
        const list = state[key];
        const index = list.findIndex(i => i.id === file.id);
        if (index >= 0) list.splice(index, 1, file);
        else list.push(file);
      });
    }
  },
  actions: {
    /**
     * Uploads files and registers them: presign, PUT to S3 four at a time,
     * then POST /mediaFile/complete. Resolves with the registered files in
     * list format — the upload is done when this resolves, no polling.
     *
     * Throws when presigning fails (quota, validation) or when no file made
     * it. With a partial success it resolves and lists the failures.
     *
     * Pass `trackStatus: false` when the caller shows its own progress (the
     * upload tray would otherwise flicker between parallel calls). Tracked
     * batches list each file in the tray, can be cancelled from it, and keep
     * the files they lose for its retry buttons.
     *
     * Optional per-file arrays, index-aligned with `files`:
     * - `names`: shown in the toast when that file fails
     * - `remarks`: each file's own remark (templates are named after their
     *   file). The server takes one remark per request, so each file is
     *   presigned on its own; uploading and registering stay batched.
     *
     * `onPresigned` fires once the server accepted the batch — see uploadBatch.
     *
     * @returns {Promise<{ completed: Object[], failed: Array<{index: number, reason: string}> }>}
     */
    async createMediaFiles({ commit, getters }, payload) {
      const {
        clinicUrl,
        branchUrl,
        data,
        files,
        onProgress,
        onPresigned,
        names,
        remarks,
        // Tray rows these files fill again (a retry); a new batch adds rows.
        slots,
        trackStatus = true
      } = payload;
      const declared = data.filesMetadata || [];
      // Sign for the bytes actually sent: images edited in the upload dialog
      // (rotate, flip) are re-encoded and no longer match their declared size.
      const contentTypes = files.map(
        (file, i) => file.type || (declared[i] && declared[i].contentType)
      );
      const publish = () => {
        if (!trackStatus) return;
        commit("setUploadStatus", {
          ...summarizeRows(trayRows),
          active: activeCancels.size > 0
        });
      };
      const cancelSource = axios.CancelToken.source();
      const cancel = {
        cancelled: false,
        token: cancelSource.token,
        isCancelled: () => cancel.cancelled,
        abort: () => {
          cancel.cancelled = true;
          cancelSource.cancel("cancelled");
        }
      };
      const context = {
        clinicUrl,
        branchUrl,
        data: { ...data, filesMetadata: undefined },
        hasRemarks: !!remarks
      };
      let slotOf = index => index;
      if (trackStatus) {
        // Nothing in flight: the tray starts over for this batch.
        if (!slots && activeCancels.size === 0) {
          trayRows = [];
          retryPool.clear();
        }
        const base = trayRows.length;
        slotOf = index => (slots ? slots[index] : base + index);
        files.forEach((file, i) => {
          trayRows[slotOf(i)] = {
            name: (names && names[i]) || file.name || `ไฟล์ ${i + 1}`,
            size: file.size || 0,
            loaded: 0,
            state: "queued",
            reason: null,
            retryable: false
          };
        });
        activeCancels.add(cancel);
        publish();
      }
      const rowOf = index => trayRows[slotOf(index)];
      const keepForRetry = index => {
        const row = rowOf(index);
        row.retryable = true;
        retryPool.set(slotOf(index), {
          file: files[index],
          meta: declared[index] || {},
          name: row.name,
          remark: remarks ? remarks[index] : undefined,
          context
        });
      };

      const presignRequest = async (indexes, remark) => {
        const res = await axios.post(
          `/api/mediaFile/getPresigned/${clinicUrl}/${branchUrl}`,
          {
            ...data,
            remark,
            filesMetadata: indexes.map(i => ({
              ...(declared[i] && declared[i].id ? { id: declared[i].id } : {}),
              contentType: contentTypes[i],
              fileSize: files[i].size
            }))
          },
          // A quota 403 is answered below with its own alert.
          { skipPermissionRedirect: true }
        );
        return res.data;
      };
      const presign = async indexes => {
        if (!remarks) return presignRequest(indexes, data.remark);
        const slotsOut = new Array(indexes.length);
        await mapWithLimit(
          indexes.map((_, i) => i),
          UPLOAD_CONCURRENCY,
          async i => {
            [slotsOut[i]] = await presignRequest([indexes[i]], remarks[indexes[i]]);
          }
        );
        return slotsOut;
      };
      const complete = async uploadIds => {
        const res = await axios.post(
          `/api/mediaFile/complete/${clinicUrl}/${branchUrl}`,
          { uploadIds },
          { skipPermissionRedirect: true }
        );
        return res.data;
      };

      try {
        const result = await uploadBatch({
          files,
          contentTypes,
          presign,
          complete,
          onPresigned,
          cancel,
          onProgress: status => {
            if (trackStatus) {
              status.items.forEach(item => {
                const row = rowOf(item.index);
                row.state = item.state;
                row.loaded = item.loaded;
              });
              publish();
            }
            if (onProgress) onProgress(status);
          }
        });
        if (trackStatus) {
          result.failed.forEach(({ index, reason }) => {
            rowOf(index).reason = reason;
          });
          // "server" means the complete call itself failed: those files are
          // in S3 and the server registers them on its own, so sending them
          // again would duplicate them. Cancelled ones were the user's call.
          result.failed
            .filter(f => f.reason !== "server" && f.reason !== "cancelled")
            .forEach(f => keepForRetry(f.index));
        }
        const completed = getters.getMediaFileListFormat(
          result.completed.map(c => c.file)
        );
        if (completed.length === 0 && result.failed.length > 0) {
          const error = new Error(`upload failed: ${result.failed[0].reason}`);
          error.failed = result.failed;
          throw error;
        }
        return { completed, failed: result.failed };
      } catch (err) {
        const msg = err.response?.data?.errorMessage;
        if (msg === "file limit exceeded")
          eventBus.$emit("alertSwal", FILE_LIMIT_EXCEED);
        if (msg === "file store plan expired.")
          eventBus.$emit("alertSwal", FILE_STORAGE_PLAN_EXPIRED);
        if (!err.failed && trackStatus) {
          files.forEach((file, i) => {
            const row = rowOf(i);
            row.state = "failed";
            if (slots) {
              // A retry the server turned away keeps its rows, ready to try again.
              row.reason = "upload";
              keepForRetry(i);
            } else {
              // A new batch turned away before sending (quota, validation):
              // the caller or the alert above explains it, so the tray hides it.
              row.reason = "rejected";
            }
          });
        }
        throw err;
      } finally {
        if (trackStatus) {
          activeCancels.delete(cancel);
          publish();
        }
      }
    },
    /**
     * Uploads again files the tracked batches lost — one tray row (`slot`)
     * or all of them — each with the batch it came from (patient, category,
     * remark), then hands the ones that made it to whichever gallery is open
     * (it filters by patient).
     */
    async retryFailedUploads({ dispatch }, { slot } = {}) {
      const slots =
        slot === undefined || slot === null
          ? [...retryPool.keys()]
          : [slot].filter(s => retryPool.has(s));
      if (slots.length === 0) return;
      // One upload per batch the files came from.
      const groups = new Map();
      slots.forEach(s => {
        const entry = retryPool.get(s);
        retryPool.delete(s);
        if (!groups.has(entry.context)) groups.set(entry.context, []);
        groups.get(entry.context).push({ ...entry, slot: s });
      });
      await Promise.all(
        [...groups.entries()].map(async ([context, entries]) => {
          try {
            const { completed } = await dispatch("createMediaFiles", {
              clinicUrl: context.clinicUrl,
              branchUrl: context.branchUrl,
              data: { ...context.data, filesMetadata: entries.map(e => e.meta) },
              files: entries.map(e => e.file),
              names: entries.map(e => e.name),
              remarks: context.hasRemarks ? entries.map(e => e.remark) : undefined,
              slots: entries.map(e => e.slot)
            });
            eventBus.$emit("mediaAdded", completed);
          } catch (error) {
            // The tray shows what failed again, with its own retry.
            console.error("Retry upload failed:", error);
          }
        })
      );
    },
    // Stops every batch in flight: files not yet sent are dropped, files
    // already in S3 are still registered.
    cancelUpload() {
      activeCancels.forEach(cancel => cancel.abort());
    },
    // Clears the tray once nothing is sending.
    dismissUploadStatus({ commit }) {
      if (activeCancels.size > 0) return;
      trayRows = [];
      retryPool.clear();
      commit("setUploadStatus", { ...IDLE_UPLOAD_STATUS, items: [] });
    },
    /**
     * Shows files returned by createMediaFiles in the gallery lists and
     * fetches their thumbnails.
     */
    addUploadedFiles({ commit, dispatch }, { clinicUrl, branchUrl, files }) {
      if (!files || files.length === 0) return;
      commit("upsertFiles", files);
      dispatch("resolveThumbnails", { clinicUrl, branchUrl, files });
    },
    async fecthMediaFiles({ commit, dispatch, getters }, payload) {
      const concat = payload.params.concat;
      delete payload.params.concat;

      const ephemeral = payload.params.ephemeral;
      delete payload.params.ephemeral;

      // Handle soft deleted parameter for recyclebin
      const softDeleted = payload.params.softDeleted;

      const res = await axios.get(
        `/api/mediaFile/${payload.clinicUrl}/${payload.branchUrl}`,
        { params: payload.params }
      );
      const data = adoptKnownThumbnails(
        getters.getMediaFileListFormat(res.data)
      );
      const { clinicUrl, branchUrl } = payload;
      if (ephemeral) {
        dispatch("resolveThumbnails", { clinicUrl, branchUrl, files: data });
        return data;
      }
      // A page appended by infinite scroll belongs to the current list; a
      // fresh fetch replaces it and retires the previous list's run.
      const generation = concat
        ? listGeneration.fileList
        : ++listGeneration.fileList;
      const isCurrent = () => listGeneration.fileList === generation;

      commit(concat ? "concatFileList" : "setFileList", data);
      dispatch("resolveThumbnails", {
        clinicUrl,
        branchUrl,
        files: data,
        isCurrent
      });
      return data;
    },

    /**
     * Fills in thumbnails for image files that do not have one yet.
     *
     * Deliberately not awaited by the caller: tiles show a placeholder until
     * their file is resolved (never the original, which would start a
     * multi-megabyte download the thumbnail was meant to avoid), then swap to
     * the thumbnail, or to the original when none can be made. Generating one
     * reads the original server-side, so this asks in small chunks.
     *
     * Every file handed in ends up with `thumbResolved` set, whatever happens,
     * so no tile is left on its placeholder.
     */
    async resolveThumbnails(
      { commit },
      { clinicUrl, branchUrl, files, isCurrent = () => true }
    ) {
      const pending = (files || []).filter(f => !f.thumbResolved);
      if (pending.length === 0) return;

      const settle = (list, urls = {}) =>
        commit(
          "applyThumbnails",
          list.map(file => ({ file, url: urls[file.id] || null }))
        );

      // Already being asked for by an earlier run (upload polling refetches
      // every few seconds): share its answer instead of asking again.
      const queue = [];
      pending.forEach(file => {
        const inflight = inflightThumbs.get(file.id);
        if (!inflight) return queue.push(file);
        inflight.then(url => settle([file], { [file.id]: url }));
      });

      for (let i = 0; i < queue.length; i += THUMBNAIL_CHUNK) {
        if (!isCurrent()) {
          // The list was replaced; nothing renders these objects any more.
          settle(queue.slice(i));
          return;
        }
        const chunk = queue.slice(i, i + THUMBNAIL_CHUNK);
        const ids = chunk.map(f => f.id);

        const request = axios
          .post(`/api/mediaFile/thumbnails/${clinicUrl}/${branchUrl}`, { ids })
          .then(res => (res.data && res.data.urls) || {});
        // Never rejects: a waiting run treats failure as "no thumbnail".
        ids.forEach(id =>
          inflightThumbs.set(
            id,
            request.then(urls => urls[id] || null, () => null)
          )
        );

        try {
          const urls = await request;
          // null means "no thumbnail" (not an image, missing, other clinic):
          // resolved, and the tile shows the original.
          ids.forEach(id => rememberThumb(id, urls[id] || null));
          settle(chunk, urls);
        } catch (error) {
          // Fall back to originals for everything left rather than leaving
          // tiles on the placeholder. Costs bandwidth, not function.
          settle(queue.slice(i));
          return;
        } finally {
          ids.forEach(id => inflightThumbs.delete(id));
        }
      }
    },
    async fecthMediaFilesTemplate({ commit, dispatch, getters }, payload) {
      const concat = payload.params?.concat;
      delete payload.params?.concat;

      const ephemeral = payload.params?.ephemeral;
      delete payload.params?.ephemeral;

      const res = await axios.get(
        `/api/mediaFile/${payload.clinicUrl}/${payload.branchUrl}`,
        {
          params: { category: "Template", ...payload.params }
        }
      );
      const data = adoptKnownThumbnails(
        getters.getMediaFileListFormat(res.data)
      );
      const { clinicUrl, branchUrl } = payload;
      if (ephemeral) {
        dispatch("resolveThumbnails", { clinicUrl, branchUrl, files: data });
        return data;
      }
      const generation = concat
        ? listGeneration.fileTemplate
        : ++listGeneration.fileTemplate;
      const isCurrent = () => listGeneration.fileTemplate === generation;

      // Templates are mostly PDFs, which are resolved up front; only image
      // templates cost a request.
      commit(concat ? "concatFileTemplate" : "setFileTemplate", data);
      dispatch("resolveThumbnails", {
        clinicUrl,
        branchUrl,
        files: data,
        isCurrent
      });
      return data;
    },
    async deleteMediaFile({ commit, state }, payload) {
      // Soft delete - move to recyclebin
      const res = await axios.delete(
        `/api/mediaFile/recycleBin/${payload.clinicUrl}/${payload.branchUrl}/${payload.id}`
      );
      if (res.status === 200) {
        const f = state.fileList.filter(i => {
          return payload.id !== i.id;
        });
        commit("setFileList", f);
        const t = state.fileTemplate.filter(i => {
          return payload.id !== i.id;
        });
        commit("setFileTemplate", t);
      }
      return res;
    },
    async restoreMediaFile({ commit, state }, payload) {
      const res = await axios.post(
        `/api/mediaFile/restore/${payload.clinicUrl}/${payload.branchUrl}/${payload.id}`
      );
      if (res.status === 200) {
        const f = state.fileList.filter(i => {
          return payload.id !== i.id;
        });
        commit("setFileList", f);
      }
      return res;
    },
    async permanentlyDeleteMediaFile({ commit, state }, payload) {
      // Using the regular delete endpoint for permanent deletion
      const res = await axios.delete(
        `/api/mediaFile/${payload.clinicUrl}/${payload.branchUrl}/${payload.id}`
      );
      if (res.status === 200) {
        const f = state.fileList.filter(i => {
          return payload.id !== i.id;
        });
        commit("setFileList", f);
      }
      return res;
    },
    async emptyRecycleBin({ commit, state }, payload) {
      // Update endpoint to match API path: /api/mediaFile/recycleBin/clear/{clinicURL}/{branchURL}
      const res = await axios.delete(
        `/api/mediaFile/clear/recycleBin/${payload.clinicUrl}/${payload.branchUrl}`
      );
      if (res.status === 200) {
        commit("setFileList", []);
      }
      return res;
    },
    async setNewFileLength({ commit, state }, payload) {
      commit("setNewFileLength", payload);
    },
    async addNewFileLength({ commit, state }, payload) {
      commit("setNewFileLength", payload);
    },
    async fetchRadiiViewUrl(context, { imageUrl }) {
      return await axios.post(
        REDIIVIEW_BASE_URL,
        { image_url: imageUrl },
        {
          withCredentials: false
        }
      );
    },
    async subscribeFileStorage({ commit }, payload) {
      const res = await axios.post(
        `/api/branch/${payload.clinicUrl}/${payload.branchUrl}/fileStorage/subscribe`,
        {
          durationMonths: payload.durationMonths,
          packageId: payload.packageId,
          confirmed: payload.confirmed !== undefined ? payload.confirmed : true
        }
      );
      return res;
    },
  },
  getters: {
    getUploadStatus: state => state.uploadStatus,
    getDownloadStatus: state => state.downloadStatus,
    getFileList: state => {
      return state.fileList;
    },
    getFileTemplate: state => {
      return state.fileTemplate;
    },

    getMediaFileListFormat: (
      state,
      getters,
      rootState,
      rootGetters
    ) => data => {
      return data.map(i => {
        return {
          id: i.id,
          src: i.mediaURL,
          // Tiles render through MediaThumb (see its rules); lightboxes,
          // printing and document embedding always use `src`. Both fields are
          // declared up front so Vue 2 reacts when they are filled in later.
          thumb: i.thumbnailURL || null,
          thumbResolved:
            !!i.thumbnailURL || !THUMBNAILABLE_TYPES.includes(i.fileType),
          title: i.remark ? i.remark : i.category,
          category: i.category,
          patientId: i.patientId,
          labId: i.labId,
          status: i.status,
          mediaType: i.fileType === "application/pdf" ? "iframe" : null,
          remark: i.remark,
          creationDt: i.creationDt,
          treatmentId: i.treatmentId,
          // for the other-branch badge and the document list's branch column
          branchId: i.branchId,
          branch: i.branch,
          fileSize: i.fileSize,
          fileType: i.fileType,
          deletedAt: i.deletedAt  // Include deletedAt field for recycle bin sorting
        };
      });
    }
  }
};
export default moduleMediaFile;
